import { parseQuery, stringifyParsedURL, stringifyQuery } from 'ufo'
import debounce from 'lodash-es/debounce'
import type {
  NavigationHookAfter,
  RouteLocationNormalizedGeneric,
  RouteLocationResolvedGeneric,
} from 'vue-router'

/**
 * Creates compatible with vue-router location object from full path or another location object.
 * A copy of the function from Nuxt stub router, types are aligned with `vue-router`.
 *
 * @param {string|Partial<RouteLocationNormalizedGeneric>} fullPath Path string or Route object
 * @returns {RouteLocationResolvedGeneric} Compatible vue-router Route object
 * @see https://github.com/nuxt/nuxt/blob/9d25602fe37a53ed93080da7489990467cb66497/packages/nuxt/src/app/plugins/router.ts#L39-L64
 */
export function getRouteFromPath(
  fullPath: string | Partial<RouteLocationNormalizedGeneric>,
): RouteLocationResolvedGeneric {
  const route = fullPath && typeof fullPath === 'object' ? fullPath : {}

  if (typeof fullPath === 'object') {
    fullPath = stringifyParsedURL({
      pathname: fullPath.path || '',
      search: stringifyQuery(fullPath.query || {}),
      hash: fullPath.hash || '',
    })
  }

  const url = new URL(
    fullPath.toString(),
    import.meta.client ? window.location.href : 'http://localhost',
  )

  return {
    path: url.pathname,
    fullPath,
    query: parseQuery(url.search),
    hash: url.hash,
    // stub properties for compat with vue-router
    params: route.params || {},
    name: undefined,
    matched: route.matched || [],
    redirectedFrom: undefined,
    meta: route.meta || {},
    href: fullPath,
  }
}

/**
 * Create copy of route with shallow meta property to indicate shallow navigation.
 *
 * @param {Route} route Original route object
 * @returns {Route} Modified route object with shallow meta property
 */
export function withShallowRoute<
  R extends Pick<RouteLocationNormalizedGeneric, 'meta'>,
>(route: R): R {
  return { ...route, meta: { ...route.meta, shallow: true } }
}

export function isShallowRoute<
  R extends Pick<RouteLocationNormalizedGeneric, 'meta'>,
>(route: R): boolean {
  return Boolean(route.meta.shallow)
}

export function onFullRouteUpdate(
  callback: NavigationHookAfter,
  options: { debounce?: number } = {},
) {
  const router = useRouter()
  let afterNavigationUnsubscribe: () => void
  let unmounted = false

  const navigationHook = ((to, from, failure) => {
    if (unmounted || isShallowRoute(to)) return
    callback(to, from, failure)
  }) as NavigationHookAfter

  const syncStateNavigation = options.debounce
    ? debounce(navigationHook, options.debounce)
    : navigationHook

  onBeforeMount(() => {
    unmounted = false
    afterNavigationUnsubscribe = router.afterEach(syncStateNavigation)
  })

  onBeforeUnmount(() => {
    unmounted = true
    afterNavigationUnsubscribe()
  })
}

/**
 * Handles page transitions to catch bfcache browser optimization.
 *
 * @see {@link https://web.dev/articles/bfcache | Back/forward cache (bfcache)}
 * @see https://jira.migros.net/browse/IHGWEBCC-2042
 */
export function onCachedPageTransition(onRestore: () => void) {
  if (!import.meta.client) return

  const handler = (event: PageTransitionEvent) => {
    if (event.persisted) {
      onRestore()
    }
  }

  onMounted(() => {
    window.addEventListener('pageshow', handler)
  })

  onUnmounted(() => {
    window.removeEventListener('pageshow', handler)
  })
}
